import { BankAccount } from '@liveauctioneers/caterwaul-components/types/BankAccount';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { BodySecondary, H1 } from '@liveauctioneers/hammer-ui-core/text';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import EnsureLoggedInContainer from '@/components/EnsureLoggedIn/EnsureLoggedInContainer';
import SimilarItemsContainer from '@/components/SimilarItems/SimilarItemsContainer';
import { PaymentMethods, PaymentProviders } from '@/enums';
import { ShippingMethods } from '@/enums/shippingMethod';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import { ExpiredCheckoutMessage } from '@/pages/Checkout/components/ExpiredMessage/ExpiredCheckoutMessage';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getDefaultBankAccount } from '@/redux/modules/account/payments/bankAccount/bankAccount.selectors';
import {
    addBidderZipCreditCard,
    clearCreditCardErrors,
} from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.actions';
import { getDefaultBidderCreditCard } from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.selectors';
import {
    getBidderEmail,
    getDefaultAddressId,
    getUserAddressById,
    isUserDataLoaded,
    isUserDataLoading,
    isUserLoggedIn,
} from '@/redux/modules/account/user/user.selectors';
import {
    getCatalogAchPayments,
    getCatalogPaymentProvider,
    getCatalogRequiredACH,
    getCatalogSellerId,
    getIsCatalogLoaded,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { fetchInvoiceIfNeeded } from '@/redux/modules/checkout/checkout.actions';
import {
    getInvoiceById,
    getInvoiceErrors,
    getIsInvoiceLoaded,
    invoiceHasNonLAPPayments,
} from '@/redux/modules/checkout/checkout.selectors';
import {
    getCheckoutShippingQuote,
    getShippingQuoteError,
} from '@/redux/modules/checkoutShippingQuote/checkoutShippingQuote.selectors';
import { getBaseUrl, getZipMerchantKey } from '@/redux/modules/config';
import { getPaymentSuccess, submitLAPayment } from '@/redux/modules/payment';
import { getFeeLoadSuccess, getFees } from '@/redux/modules/processingFee';
import { mergedCaterwaulAndHammerUIThemes } from '@/theme/mergedCaterwaulAndHammerUIThemes';
import { BidderAddress } from '@/types/BidderAddresses';
import { CreditCard } from '@/types/CreditCard';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getCheckoutUrlATG, getIdFromPath, getWonItemsPageUrl, toQueryString } from '@/utils/urls';

import { CheckoutPageHeader } from './components/CheckoutPageHeader/CheckoutPageHeader';
import { PaymentProcessedNotification } from './components/PaymentProcessNotification/PaymentProcessedNotification';
import { RowDivider } from './components/RowDivider';
import Steps from './components/Steps/Steps';
import { InvoiceSummary } from './components/Summary/InvoiceSummary';
import { InvoiceSummaryItems } from './components/Summary/InvoiceSummaryItems';
import { getCheckoutPageData } from './helpers/checkoutPageData';

const FIVE_THOUSAND_DOLLARS_IN_CENTS = 5000_00;

const CheckoutPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { pathname } = location;
    const formatMessage = useAppFormatMessage();
    const dispatch = useAppDispatch();

    const userIsLoggedIn = useAppSelector(isUserLoggedIn);

    // Prompt user to login and return to this page
    useEffect(() => {
        if (!userIsLoggedIn) {
            navigate(
                `/loginRedirect?${toQueryString({
                    redirectTo: `${location.pathname}${location.search || ''}`,
                })}`
            );
        }
    }, [location, userIsLoggedIn, navigate]);

    const {
        breakpoints: { tabletWidthNum },
    } = mergedCaterwaulAndHammerUIThemes;
    const showInvoiceSummaryItems = useIsNarrowerThan(tabletWidthNum + 1);

    const catalogId = getIdFromPath(pathname);
    const baseUrl = useAppSelector(getBaseUrl);
    const email = useAppSelector(getBidderEmail);

    const catalogLoaded = Boolean(useAppSelector((state) => getIsCatalogLoaded(state, catalogId)));
    const loadInvoiceError = useAppSelector((state) => getInvoiceErrors(state, catalogId));
    const userDataLoaded = useAppSelector(isUserDataLoaded);
    const userDataLoading = useAppSelector(isUserDataLoading);
    const achPayments = useAppSelector((state) => getCatalogAchPayments(state, catalogId));
    const paymentProvider = useAppSelector((state) => getCatalogPaymentProvider(state, catalogId));
    const requiredACH = useAppSelector((state) => getCatalogRequiredACH(state, catalogId));
    const houseId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const defaultAddressId = useAppSelector(getDefaultAddressId);
    const defaultAddress = useAppSelector((state) => getUserAddressById(state, defaultAddressId));
    const zipMerchantKey = useAppSelector(getZipMerchantKey);
    const defaultCreditCard = useAppSelector((state) => getDefaultBidderCreditCard(state, paymentProvider));
    const defaultBankAccount = useAppSelector((state) => getDefaultBankAccount(state, paymentProvider));
    const shippingQuote = useAppSelector(getCheckoutShippingQuote);
    const quoteErrorCode = useAppSelector((state) => getShippingQuoteError(state));
    const laShipping = shippingQuote.shippingAvailable;
    const { invoice, invoiceAddress, items } = useAppSelector((state) => getInvoiceById(state, catalogId, laShipping));
    const invoiceLoaded = useAppSelector((state) => getIsInvoiceLoaded(state, catalogId));
    const hasNonLAPayments = useAppSelector((state) => invoiceHasNonLAPPayments(state, catalogId, laShipping));
    const processingFees = useAppSelector((state) => getFees(state, catalogId));
    const processingFeesLoaded = useAppSelector(getFeeLoadSuccess);

    const showAchPaymentOption = paymentProvider === PaymentProviders.Payrix && achPayments;

    const [amountToPay, setAmountToPay] = useState(0);
    const [invoiceTotalAmount, setInvoiceTotalAmount] = useState(0);
    const [amountToPayZip, setAmountToPayZip] = useState(0);
    const [hadZipError, setHadZipError] = useState(false);
    const [installmentFee, setInstallmentFee] = useState(0);
    const [onReviewStep, setOnReviewStep] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState(PaymentMethods.CreditCard);
    const [paymentMethodValid, setPaymentMethodValid] = useState(false);
    const [paymentToken, setPaymentToken] = useState(defaultCreditCard?.token);
    const [readOnly, setReadOnly] = useState(false); // checkout - false, confirmation - true
    const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccount>();
    const [selectedCreditCard, setSelectedCreditCard] = useState<CreditCard>();
    const [shippingAddress, setShippingAddress] = useState(invoiceAddress?.addressId ? invoiceAddress : defaultAddress);
    const [shippingAddressValid, setShippingAddressValid] = useState(Boolean(shippingAddress?.addressId));
    const [shippingMethod, setShippingMethod] = useState(invoice.shippingMethod);
    const [shippingMethodValid, setShippingMethodValid] = useState(Boolean(shippingMethod));
    const [zipLoaded, setZipLoaded] = useState(false);
    const [requireACHForCheckout, setRequireACHForCheckout] = useState(false);
    const [ready, setReady] = useState(false);
    const wasPaymentSuccessful = useAppSelector(getPaymentSuccess);
    const [activeProcessingFee, setActiveProcessingFee] = useState(processingFees.ccFee);

    const showCardPaymentOption = !requireACHForCheckout;
    const {
        buyersPremium,
        currency,
        houseShippingQuote,
        itemTotal,
        otherCharges,
        payments,
        paymentsTotal,
        salesTax,
        shipping,
    } = invoice;

    const hideShipping = !laShipping && !invoice.shipping && !invoice.houseShippingQuote;

    const showZipOption =
        paymentProvider === PaymentProviders.Payrix &&
        amountToPay < FIVE_THOUSAND_DOLLARS_IN_CENTS &&
        Boolean(shippingAddress?.state) &&
        zipLoaded &&
        amountToPayZip !== 0 &&
        !requireACHForCheckout &&
        !hadZipError;

    const { itemId } = items?.[0] || {};
    const loaded = userDataLoaded && invoiceLoaded && catalogLoaded && processingFeesLoaded;

    useEffect(() => {
        if (loaded && invoice?.atgpay) {
            navigate(getCheckoutUrlATG(catalogId));
        }
    }, [catalogId, loaded, invoice.atgpay, navigate]);

    // prevent processing fee from being shown to bidder when appropriate
    // 0 no payments, 1 house absorbing fee, 2 house passing fee.
    const bidderPaysProcessingFee = invoice.laPayment === 2;

    const onBankAccountSelectionChange = useCallback(
        (bankAccount: BankAccount) => {
            if (bankAccount) {
                setPaymentMethodValid(true);
                if (paymentMethod === PaymentMethods.Ach) {
                    setSelectedBankAccount(bankAccount);
                    setPaymentToken(bankAccount.token);
                }
            } else {
                setPaymentMethodValid(false);
                setPaymentToken(undefined);
                setSelectedBankAccount(undefined);
            }
        },
        [paymentMethod]
    );

    const onCardSelectionChange = useCallback(
        (creditCard: CreditCard) => {
            if (creditCard) {
                setPaymentMethodValid(true);
                if (paymentMethod === PaymentMethods.CreditCard) {
                    setSelectedCreditCard(creditCard);
                    setPaymentToken(creditCard.token);
                }
            } else {
                setPaymentMethodValid(false);
                setPaymentToken(undefined);
                setSelectedCreditCard(undefined);
            }
        },
        [paymentMethod]
    );

    const onPaymentMethodChange = useCallback(
        (paymentMethod: PaymentMethods) => {
            let valid = false;
            let token;
            if (paymentMethod === PaymentMethods.Ach) {
                valid = Boolean(selectedBankAccount);
                token = selectedBankAccount?.token;
            } else if (paymentMethod === PaymentMethods.CreditCard) {
                valid = Boolean(selectedCreditCard);
                token = selectedCreditCard?.token;
            } else if (paymentMethod === PaymentMethods.Zip) {
                valid = true;
            }
            setPaymentMethod(paymentMethod);
            setPaymentMethodValid(valid);
            setPaymentToken(token);
        },
        [selectedBankAccount, selectedCreditCard]
    );

    const onShippingChange = useCallback((shippingAddress: BidderAddress) => {
        setShippingAddress(shippingAddress);
        setShippingAddressValid(Boolean(shippingAddress?.addressId));
    }, []);

    const onShippingMethodChange = useCallback((shippingMethod: ShippingMethods, shippingMethodValid: boolean) => {
        setShippingMethod(shippingMethod);
        setShippingMethodValid(shippingMethodValid);
    }, []);

    const submitCheckout = useCallback(
        (token?: string) => {
            let method = paymentMethod;
            let tokenToUse = paymentToken;
            let installmentFeeToUse = 0;
            if (method === PaymentMethods.Zip) {
                method = PaymentMethods.CreditCard;
                tokenToUse = token;
                installmentFeeToUse = installmentFee;
            }
            return Promise.all([
                dispatch(
                    submitLAPayment({
                        installmentFee: installmentFeeToUse,
                        invoiceId: invoice.id,
                        paymentMethod: method,
                        paymentToken: tokenToUse,
                        phoneNumber: shippingAddress?.phoneNumber,
                        shippingAddress,
                        shippingMethod: shippingMethod.toLowerCase(),
                    })
                ),
            ])
                .then(() => {
                    dispatch(fetchInvoiceIfNeeded({ catalogId, force: true }));
                })
                .catch(() => {
                    // we don't need to dispatch anything on payment failure
                });
        },
        [catalogId, dispatch, installmentFee, invoice.id, paymentMethod, paymentToken, shippingAddress, shippingMethod]
    );

    useEffect(() => {
        setReadOnly(wasPaymentSuccessful);
        dispatch(clearCreditCardErrors());
    }, [wasPaymentSuccessful, dispatch]);

    const handleSubmitClick = () => {
        if (zipLoaded && paymentMethod === PaymentMethods.Zip && Boolean(shippingAddress)) {
            //https://docs.quadpay.com/docs/virtual-checkout#open-checkout-required
            const amountToPayInDollars = amountToPay / 100;
            window.quadpay?.virtualCheckout.openCheckout(
                zipMerchantKey,
                {
                    amount: amountToPayInDollars,
                    currency: invoice.currency.toUpperCase(),
                    customer: {
                        address1: shippingAddress?.address1,
                        address2: shippingAddress?.address2,
                        city: shippingAddress?.city,
                        country: shippingAddress?.countryCode,
                        email: email,
                        firstName: shippingAddress?.firstName,
                        lastName: shippingAddress?.lastName,
                        phoneNumber: shippingAddress?.phoneNumber,
                        postalCode: shippingAddress?.postalCode,
                        state: shippingAddress?.state,
                    },
                    merchantReference: uuidv4(),
                },
                'standard'
            );
        } else {
            submitCheckout();
        }
    };

    const onZipComplete = useCallback(
        (result) => {
            //https://docs.quadpay.com/docs/virtual-checkout#on-complete-callback-handler

            dispatch(
                addBidderZipCreditCard({
                    billingAddress: {
                        address1: result.cardholder?.address1,
                        address2: result.cardholder?.address2,
                        city: result.cardholder.city,
                        countryCode: result.cardholder.country,
                        firstName: result.cardholder.firstName,
                        lastName: result.cardholder.lastName,
                        postalCode: result.cardholder.postalCode,
                        state: result.cardholder.state,
                    },
                    brand: result.card.brand,
                    firstName: result.cardholder.firstName,
                    houseId: houseId,
                    lastName: result.cardholder.lastName,
                    month: result.card.expirationMonth,
                    number: result.card.number,
                    providerId: paymentProvider,
                    securityCode: result.card.cvc,
                    year: result.card.expirationYear,
                })
            )
                .unwrap()
                .then((zipCard) => {
                    submitCheckout(zipCard.token);
                    setReadOnly(true);
                });
        },
        [dispatch, houseId, paymentProvider, submitCheckout]
    );

    const onZipError = useCallback(
        (errorMessage: string) => {
            //https://docs.quadpay.com/docs/virtual-checkout#on-error-callback-handler
            setHadZipError(Boolean(errorMessage));
            onPaymentMethodChange(PaymentMethods.CreditCard);
        },
        [onPaymentMethodChange]
    );

    const onSetZipLoaded = () => {
        if (!zipLoaded) {
            setZipLoaded(true);
        }
    };

    // Used only for the useEffect below.
    const [hasSetInitialAddress, setHasSetInitialAddress] = useState(false);
    useEffect(() => {
        // If invoice address does not match AND we haven't forcefully set it once.
        if (invoiceAddress?.addressId !== shippingAddress?.addressId && !hasSetInitialAddress) {
            setShippingAddress(invoiceAddress);
            setShippingMethod(invoice.shippingMethod);
        }

        // Only set has set initial address if the invoice address has actually been loaded.
        if (invoiceLoaded) {
            setHasSetInitialAddress(true);
        }
    }, [hasSetInitialAddress, invoice.shippingMethod, invoiceAddress, invoiceLoaded, shippingAddress]);

    //handle initial load and load of different checkout pages
    useEffect(() => {
        if (!userDataLoading && userDataLoaded) {
            dispatch(getCheckoutPageData(catalogId, false));
        }
    }, [catalogId, dispatch, userDataLoaded, userDataLoading]);

    //handle initial setting of state
    useEffect(() => {
        if (loaded) {
            const isReadOnly = invoice?.balanceDue === 0;
            setReadOnly(isReadOnly);

            let amountToPay = invoice.balanceDueSubtotal * 100;
            let invoiceTotalAmount = invoice.invoiceTotal;
            const invoiceTotalsWithFees = {
                invoiceTotalMinusShipping: invoiceTotalAmount - invoice.houseShippingQuote,
                invoiceTotalWithACHFee: readOnly
                    ? invoiceTotalAmount
                    : invoiceTotalAmount + processingFees.achFee / 100,
                invoiceTotalWithACHHightRiskFee: readOnly
                    ? invoiceTotalAmount
                    : invoiceTotalAmount + processingFees.achHrFee / 100,
                invoiceTotalWithCreditCardFee: readOnly
                    ? invoiceTotalAmount
                    : invoiceTotalAmount + processingFees.ccFee / 100,
                invoiceTotalWithCreditCardHighRiskFee: readOnly
                    ? invoiceTotalAmount
                    : invoiceTotalAmount + processingFees.hrFee / 100,
            };

            if (invoice.shippingMethod !== ShippingMethods.HouseShipping) {
                amountToPay = hideShipping ? amountToPay - invoice.houseShippingQuote : amountToPay;

                invoiceTotalAmount = invoiceTotalsWithFees.invoiceTotalMinusShipping;
            }

            if (paymentMethod === PaymentMethods.Ach) {
                amountToPay = processingFees.balanceWithACHFee;
                if (bidderPaysProcessingFee) {
                    invoiceTotalAmount = invoiceTotalsWithFees.invoiceTotalWithACHFee;
                    if (invoice.highRisk) {
                        invoiceTotalAmount = invoiceTotalsWithFees.invoiceTotalWithACHHightRiskFee;
                        amountToPay = processingFees.balanceWithACHHRFee;
                    }
                }
            } else if (
                paymentMethod === PaymentMethods.CreditCard &&
                (paymentProvider === PaymentProviders.Paysafe || paymentProvider === PaymentProviders.Payrix)
            ) {
                if (invoice.highRisk) {
                    amountToPay = processingFees.balanceWithHRFee;
                    if (bidderPaysProcessingFee) {
                        invoiceTotalAmount = invoiceTotalsWithFees.invoiceTotalWithCreditCardHighRiskFee;
                    }
                } else {
                    amountToPay = processingFees.balanceWithCCFee;
                    if (bidderPaysProcessingFee) {
                        invoiceTotalAmount = invoiceTotalsWithFees.invoiceTotalWithCreditCardFee;
                    }
                }
            } else if (paymentMethod === PaymentMethods.Zip) {
                amountToPay = processingFees.balanceWithCCFee;
                if (bidderPaysProcessingFee) {
                    invoiceTotalAmount = invoiceTotalsWithFees.invoiceTotalWithCreditCardFee;
                }
            }
            if (amountToPay > 0 && Boolean(shippingAddress?.state) && zipLoaded) {
                window.quadpay?.virtualCheckout.onComplete(onZipComplete);
                window.quadpay?.virtualCheckout.onError(onZipError);
                //always calculate zip based off of credit card so the on page display appears correct
                const amountToPayInDollarsAndCents = processingFees.balanceWithCCFee / 100;
                //will be in dollars and cents
                let amountToPayZip = 0;
                //will be in dollars and cents
                let installmentFee = 0;
                window.quadpay?.merchantFees
                    .calculateMerchantFeeForPaymentPlan({
                        amount: amountToPayInDollarsAndCents,
                        currency: invoice.currency.toUpperCase(),
                        customerCountry: shippingAddress?.countryCode,
                        customerState: shippingAddress?.state,
                        merchantId: zipMerchantKey,
                    })
                    .then((result) => {
                        installmentFee = result.merchantFeeForPaymentPlan;
                        const installments = window.quadpay?.calculateInstallments(
                            amountToPayInDollarsAndCents + installmentFee
                        );
                        amountToPayZip = installments[0].amount;
                        setAmountToPayZip(amountToPayZip);
                        setInstallmentFee(installmentFee);
                    })
                    .catch(() => {
                        amountToPayZip = 0;
                        installmentFee = 0;
                        setAmountToPayZip(amountToPayZip);
                        setInstallmentFee(installmentFee);
                    });
            }
            setAmountToPay(amountToPay);
            setInvoiceTotalAmount(invoiceTotalAmount);

            //When requiredACH is 0, users may select their payment option. When set, any balance over this number(in cents) must use ach.
            //> instead of >= because they are both 0 before loading.
            const requireACHForCheckout = amountToPay !== 0 && requiredACH !== 0 && amountToPay > requiredACH;
            setRequireACHForCheckout(requireACHForCheckout);

            if (!shippingAddress) {
                onShippingChange(invoiceAddress?.addressId ? invoiceAddress : defaultAddress);
            }

            if (!selectedCreditCard && Boolean(defaultCreditCard)) {
                onCardSelectionChange(defaultCreditCard);
            }

            if (!selectedBankAccount && Boolean(defaultBankAccount)) {
                onBankAccountSelectionChange(defaultBankAccount);
            }

            if (requireACHForCheckout && paymentMethod !== PaymentMethods.Ach) {
                onPaymentMethodChange(PaymentMethods.Ach);
                setPaymentMethodValid(false);
            }

            if (!ready) {
                setReady(true);
            }
        }
    }, [
        catalogId,
        bidderPaysProcessingFee,
        defaultAddress,
        defaultBankAccount,
        defaultCreditCard,
        dispatch,
        hideShipping,
        invoice,
        invoiceAddress,
        loaded,
        onBankAccountSelectionChange,
        onCardSelectionChange,
        onPaymentMethodChange,
        onZipComplete,
        onZipError,
        paymentMethod,
        paymentProvider,
        processingFees,
        ready,
        readOnly,
        requiredACH,
        selectedBankAccount,
        selectedCreditCard,
        shippingAddress,
        shippingAddressValid,
        shippingMethod,
        zipLoaded,
        zipMerchantKey,
        location,
        onShippingChange,
    ]);

    // handle shipping method
    useEffect(() => {
        const loadedWithoutShippingMethod =
            invoiceLoaded && Boolean(invoice) && (!shippingMethod || !shippingMethodValid);
        if (!loadedWithoutShippingMethod) {
            return;
        }

        if (
            invoice.shippingMethod === ShippingMethods.Pickup ||
            invoice.shippingMethod === ShippingMethods.BidderShipping ||
            invoice.shippingMethod !== ShippingMethods.LiveShipping
        ) {
            onShippingMethodChange(invoice.shippingMethod, true);
        } else if (!quoteErrorCode) {
            onShippingMethodChange(ShippingMethods.LiveShipping, true);
        } else if (shippingQuote?.link) {
            onShippingMethodChange(ShippingMethods.HouseShipping, true);
        } else {
            onShippingMethodChange(ShippingMethods.BidderShipping, true);
        }
    }, [
        catalogId,
        dispatch,
        invoice,
        invoiceLoaded,
        onShippingMethodChange,
        quoteErrorCode,
        shippingMethod,
        shippingMethodValid,
        shippingQuote?.link,
    ]);

    //update the processing fees
    useEffect(() => {
        if (paymentMethod === PaymentMethods.Ach) {
            if (invoice.highRisk) {
                setActiveProcessingFee(processingFees.achHrFee);
            } else {
                setActiveProcessingFee(processingFees.achFee);
            }
        } else {
            if (invoice.highRisk) {
                setActiveProcessingFee(processingFees.hrFee);
            } else {
                setActiveProcessingFee(processingFees.ccFee);
            }
        }
    }, [
        invoice.highRisk,
        paymentMethod,
        processingFees.achFee,
        processingFees.achHrFee,
        processingFees.ccFee,
        processingFees.hrFee,
    ]);

    //handle error redirect, this happens when the user tries to load an invoice that is for a different user, or doesn't exist.
    //If they are not logged in, the login prompt will still happen as expected.
    useEffect(() => {
        if ('No Invoice for catalog' === loadInvoiceError) {
            // Redirect to won items page instead of showing an error
            navigate(getWonItemsPageUrl());
        }
    }, [loadInvoiceError, navigate]);

    //The shippingAmount is used for calculation of the shipping quote, NOT for display on the order summary.
    //We use houseShippingQuote because it is always set, while shipping changes based on the saved shipping method.
    //The order summary always displays the real shipping charge.
    const shippingAmount = shippingMethod === ShippingMethods.HouseShipping ? shipping || houseShippingQuote : 0;
    let readyToPay = Boolean(shippingAddress?.addressId) && paymentMethodValid && onReviewStep && shippingMethodValid;
    let invalidCheckout = false;

    if ((paymentProvider === PaymentProviders.Unknown || paymentProvider === PaymentProviders.Stripe) && !readOnly) {
        readyToPay = false;
        invalidCheckout = true;
    }

    if (!ready) {
        return (
            <EnsureLoggedInContainer>
                {/* the &_div is because the Loading component has a div
                 * surrounding it that cannot have styles applied to it
                 */}
                <div className="mt-3xl flex w-full [&_div]:mx-auto">
                    <Loading size={64} />
                </div>
            </EnsureLoggedInContainer>
        );
    }

    return (
        <>
            <CheckoutPageHeader
                baseUrl={baseUrl}
                catalogId={catalogId}
                houseId={houseId}
                setZipLoaded={onSetZipLoaded}
            />
            <div className="mx-auto my-0 max-w-[1240px] px-lg py-0">
                {invalidCheckout ? (
                    <ExpiredCheckoutMessage
                        message={formatMessage(['checkout', 'general', 'notPayable'], {
                            values: {
                                email: (
                                    <Link
                                        key="checkout-general-not-payable-email-link"
                                        href="mailto:info@liveauctioneers.com?subject=Expired Checkout - Arrange Payment"
                                    >
                                        info@liveauctioneers.com
                                    </Link>
                                ),
                            },
                        })}
                    />
                ) : hasNonLAPayments ? (
                    <ExpiredCheckoutMessage
                        message={formatMessage(['checkout', 'general', 'notPayableNonLAP'], {
                            values: {
                                email: (
                                    <Link
                                        key="checkout-general-not-payable-non-la-email-link"
                                        href="mailto:info@liveauctioneers.com?subject=Expired Checkout - Arrange Payment"
                                    >
                                        info@liveauctioneers.com
                                    </Link>
                                ),
                            },
                        })}
                    />
                ) : (
                    <section className="mb-3xl mdMin:grid mdMin:grid-cols-[auto_64px_384px]">
                        <section>
                            <div className="mb-lg smMax:mb-sm">
                                {readOnly ? (
                                    <>
                                        <Link href="/">{formatMessage(['checkout', 'back'])}</Link>
                                        <H1>{formatMessage(['checkout', 'readOnlyTitle'])}</H1>
                                        <BodySecondary>
                                            <FormattedMessage id="checkout.confirmationSubtitle" />
                                        </BodySecondary>
                                        <PaymentProcessedNotification
                                            houseId={Number(houseId)}
                                            shippingMethod={shippingMethod}
                                        />
                                    </>
                                ) : (
                                    <H1>{formatMessage(['checkout', 'title'])}</H1>
                                )}
                            </div>
                            {showInvoiceSummaryItems && (
                                <InvoiceSummaryItems
                                    className="mt-sm mb-lg bg-background-tertiary px-sm py-lg"
                                    currency={currency}
                                    items={items}
                                />
                            )}
                            <RowDivider />
                            <Steps
                                amountToPayZip={amountToPayZip}
                                bidderPaysProcessingFee={bidderPaysProcessingFee}
                                catalogId={catalogId}
                                currency={currency}
                                hadZipError={hadZipError}
                                invoiceId={invoice.id}
                                isNewCheckout={invoice.payments.length === 0}
                                items={items}
                                laShipping={laShipping}
                                onBankAccountSelectionChange={onBankAccountSelectionChange}
                                onCardSelectionChange={onCardSelectionChange}
                                onPaymentMethodChange={onPaymentMethodChange}
                                onShippingAddressChanged={onShippingChange}
                                onShippingMethodChange={onShippingMethodChange}
                                paymentMethod={paymentMethod}
                                paymentMethodValid={paymentMethodValid}
                                paymentProvider={paymentProvider}
                                readOnly={readOnly}
                                requireACHForCheckout={requireACHForCheckout}
                                selectedBankAccount={selectedBankAccount}
                                selectedCreditCard={selectedCreditCard}
                                setOnReviewStep={setOnReviewStep}
                                shippingAddress={shippingAddress}
                                shippingAddressValid={shippingAddressValid}
                                shippingAmount={shippingAmount}
                                shippingMethod={shippingMethod}
                                shippingMethodValid={shippingMethodValid}
                                shippingQuote={shippingQuote}
                                showAchPaymentOption={showAchPaymentOption}
                                showCardPaymentOption={showCardPaymentOption}
                                showZipOption={showZipOption}
                            />
                        </section>
                        {/* this is the spacer, the 64px column defined in grid-cols of the parent section */}
                        <section />
                        <section>
                            <InvoiceSummary
                                activeProcessingFee={activeProcessingFee}
                                amountToPay={amountToPay}
                                amountToPayZip={paymentMethod === PaymentMethods.Zip && amountToPayZip}
                                bidderPaysProcessingFee={bidderPaysProcessingFee}
                                buyersPremium={buyersPremium}
                                catalogId={catalogId}
                                currency={currency}
                                handleSubmitClick={handleSubmitClick}
                                hideShipping={hideShipping}
                                houseShippingQuote={houseShippingQuote}
                                installmentFee={paymentMethod === PaymentMethods.Zip && installmentFee}
                                invoiceId={invoice.id}
                                invoiceTotal={invoiceTotalAmount}
                                items={items}
                                itemTotal={itemTotal}
                                otherCharges={otherCharges}
                                paymentMethod={paymentMethod}
                                paymentProvider={paymentProvider}
                                payments={payments}
                                paymentsTotal={paymentsTotal}
                                readOnly={readOnly}
                                readyToPay={readyToPay}
                                salesTax={salesTax}
                                shipping={shipping}
                                shippingMethod={shippingMethod}
                            />
                        </section>
                    </section>
                )}
                {readOnly && (
                    <section className="mb-3xl smMax:mb-lg">
                        <SimilarItemsContainer
                            centerHeader
                            itemId={parseInt(itemId)}
                        />
                    </section>
                )}
            </div>
        </>
    );
};

export default CheckoutPage;
